import { StructuredOperationForm } from '../types/operations';
import { mapReceivableToPayload, buildInstallmentPayload, mapDisbursementToPayload } from './dataMappers';
import { sanitizeFileName } from './operationHelpers';
import { EMPTY_BILLING_CONFIG } from '../components/BillingConfigSection';

interface PayloadBuilderParams {
    form: StructuredOperationForm;
    operationType: string;
    creditOperationId?: string;
    selectedBeneficiary: string;
    lastInstallmentDueDate?: string;
    participants: unknown[];
    artifactFiles: File[];
    contractFiles: File[];
    existingArtifactFiles: string[];
    removedArtifactFiles: string[];
    existingContractFile: string;
    removedContractFile: boolean;
    contractGuarantee?: {
        contract_value: number;
        anticipated_value: number;
        file: File | null;
        existing_file: string;
    };
}

export const buildStructuredOperationPayload = ({
    form,
    operationType,
    creditOperationId,
    selectedBeneficiary,
    lastInstallmentDueDate,
    participants,
    artifactFiles,
    contractFiles,
    existingArtifactFiles,
    removedArtifactFiles,
    existingContractFile,
    removedContractFile,
    contractGuarantee,
}: PayloadBuilderParams) => {
    const dueDateValue = lastInstallmentDueDate || form.expiration_date || null;

    return {
        type: operationType || "STRUCTURED_OPERATION",
        assignor: "00000000-0000-0000-0000-000000000000",
        recipient: selectedBeneficiary,
        credit_operation_id: creditOperationId,
        operation_number: form.operation_number || "",
        originator: form.originator || "",
        with_recourse: form.with_recourse || false,
        operation_type: "STRUCTURED_OPERATION",
        operation_schema_id: 3,
        approved_on: null,
        approved_value: null,
        current_balance: null,
        debt_balance_id: null,
        nominal_value: form.nominal_value,
        loan_amount: form.loan_amount || 0,
        due_date: dueDateValue,
        expiration_date: form.expiration_date || null,
        participants,
        operation_dictionary: {
            assignor: "00000000-0000-0000-0000-000000000000",
            recipient: selectedBeneficiary,
            emission_date: form.emission_date ? form.emission_date.split('/').reverse().join('-') : "",
            approved_on: form.approved_on ? form.approved_on.split('/').reverse().join('-') : "",
            guarantee_value: form.guarantee_value,
            rebate_amount: form.rebate_amount || 0,
            fees: {
                items: form.fees.items.map(fee => ({
                    rate: fee.rate || 0,
                    amount: fee.amount || 0,
                    form: fee.form || "",
                    name: fee.name || "",
                    interest_period: fee.interest_period || ""
                })),
                quantity: form.fees.items.length
            },
            fiduciary_alienation: form.fiduciary_alienation.map(item => ({
                name: item.name || "",
                value: item.value || 0,
                guarantee_type: item.guarantee_type || "AF",
                ...(item.description && { description: item.description })
            })),
            disbursement: {
                disbursements: form.disbursement.disbursements.map(mapDisbursementToPayload),
                totalAmount: form.disbursement.totalAmount || 0
            },
            installment: buildInstallmentPayload(form.installments),
            receivables: {
                items: (form.receivables?.items || []).map(mapReceivableToPayload),
                total: form.receivables?.total || 0,
                quantity: form.receivables?.quantity || 0,
                spreadsheet_processed: form.receivables?.spreadsheet_processed || false,
                overcollateral: form.receivables?.overcollateral ?? null
            },
            operation_number: form.operation_number || "",
            external_link: form.external_link || "",
            operation_uf: form.operation_uf || 0,
            operation_city: form.operation_city || "",
            credit_product_type_name: form.credit_product_type_name || "",
            receiving_account_name: form.receiving_account_name || "",
            main_risk: {
                uuid: form.main_risk.uuid || "",
                name: form.main_risk.name || ""
            },
            credit_product_type: "STRUCTURED_OPERATION",
            is_settled: false,
            artifacts: [
                {
                    list: "DOCUMENTOS",
                    files: artifactFiles.map(file => sanitizeFileName(file.name))
                }
            ],
            description_product: {
                id: form.description_product?.id || "",
                name: form.description_product?.name || ""
            },
            base_calculation: form.base_calculation ? {
                tir_annual: form.base_calculation.tir_annual,
                tir_monthly: form.base_calculation.tir_monthly,
                spread_annual: form.base_calculation.spread_annual,
                vpl: form.base_calculation.vpl,
                vpl_margin: form.base_calculation.vpl_margin,
                duration_days: form.base_calculation.duration_days
            } : undefined,
            billing_config: form.billing_config || EMPTY_BILLING_CONFIG,
            files: {
                contract_file: contractFiles.length > 0
                    ? `${form.sequencial_number}${sanitizeFileName(contractFiles[0].name.substring(contractFiles[0].name.lastIndexOf('.')))}`
                    : (removedContractFile ? '' : existingContractFile),
                artefacts: [
                    ...existingArtifactFiles.filter(file => !removedArtifactFiles.includes(file)),
                    ...artifactFiles.map(file => sanitizeFileName(file.name))
                ]
            },
            ...(contractGuarantee && form.credit_product_type_name === 'CONTRACT_ASSIGNMENT' ? {
                contract_guarantee: {
                    contract_value: contractGuarantee.contract_value,
                    anticipated_value: contractGuarantee.anticipated_value,
                    file_name: contractGuarantee.file
                        ? sanitizeFileName(contractGuarantee.file.name)
                        : contractGuarantee.existing_file || ''
                }
            } : {})
        }
    };
};
